import * as React from "react";
import * as _ from "lodash";
import { toast } from "react-toastify";
import { useDispatch } from "redux-react-hook";

import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import { Text } from "src/library/components/Text/Text";
import Table, {
  ITableInstance,
  BulkSelectState
} from "src/library/components/Tables/Table";
import { TabbedNavigation } from "src/library/components/TabbedNavigation";
import { TabOptions } from "src/library/components/TabbedNavigation/TabbedNavigation";
import { IAction } from "src/library/components/Button/ActionButton";
import {
  useProcessJobsApi,
  IProcessJob,
  IProcessJobError
} from "src/core/process-job/api/processJobs.api";
import { PageProps } from "src/pages/Router/routes";
import {
  Layout,
  MainContentWrapper,
  InfoBarWrapper
} from "src/core/shared/components/Layout";
import { ICustomer } from "src/core/customers/api/customers.api";
import { ProcessJobSidebar } from "src/core/process-job/components/ProcessJobSideBar";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import RelatedOrdersTab from "src/core/process-job/components/RelatedOrdersTab";
import ImportedOrdersTab from "src/core/process-job/components/ImportedOrdersTab";
import { getErrorRowColumnsByJob } from "src/core/shared/components/tables/process-job-errors";

interface OrderImportPageRouteProps {
  processJobId: string;
}

const defaultPagination: PaginatedParams<ICustomer> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "id", "ASC" ] ]
};

const ViewQueueJobPage: React.ReactFCWithChildren<PageProps<unknown, OrderImportPageRouteProps>> = props => {
  const dispatch = useDispatch();

  const processJobId = React.useMemo(() => {
    return parseInt(props.match?.params.processJobId as string, 10);
  }, [ props.match?.params.processJobId ]);

  // all the api requests available to this page
  const [ getProcessJobResponse, getProcessJobRequest ] = useProcessJobsApi("GET_PROCESS_JOB");
  const [ ignoreErrorResponse, ignoreErrorRequest ] = useProcessJobsApi("BULK_IGNORE_PROCESS_JOB_ERROR");
  const [ retryErrorResponse, retryErrorRequest ] = useProcessJobsApi("BULK_RETRY_PROCESS_JOB_ERROR");

  const [ getProcessJobErrorsResponse, getProcessJobErrorsRequest ] = useProcessJobsApi(
    "GET_PROCESS_JOB_ERRORS"
  );

  // table refs
  const TableRef = React.useRef<ITableInstance<IProcessJobError>>(null);
  const [ processJob, setProcessJob ] = React.useState<IProcessJob | null>(null);
  const isLoading = ignoreErrorResponse.pending || getProcessJobResponse.pending || getProcessJobErrorsResponse.pending;
  const importInterval = React.useRef<NodeJS.Timeout | boolean>(false);

  // variables to load data conditional on job type
  const hasOrders = React.useMemo(() => {
    if (!processJob) {
      return false;
    }

    return [
      "import-file-order-pick",
      "import-file-order",
      "global-product-replacement"
    ].includes(processJob.name);
  }, [ processJob ]);

  const hasWarnings = React.useMemo(() => {
    return processJob?.errors?.some(error => error.status === "warn");
  }, [ processJob?.errors ]);

  const hasErrors = React.useMemo(() => {
    return processJob?.errors?.some(error => error.status === "error");
  }, [ processJob?.errors ]);

  // variables to load data conditional on job type
  const canRetryErrors = React.useMemo(() => {
    if (!processJob) {
      return false;
    }

    return [ "import-file-order-pick", "import-file-order" ].includes(processJob.name);
  }, [ processJob ]);

  const reloadJobData = React.useCallback(
    (refreshErrors = false) => {
      if (typeof processJobId === "number") {
        getProcessJobRequest(processJobId);
      }

      if (refreshErrors) {
        if (TableRef.current) {
          TableRef.current.resetPagination();
          TableRef.current.reloadTable();
        }
      }
    },
    [ processJobId ]
  );

  // reload when
  React.useEffect(() => {
    if (typeof processJobId === "number") {
      reloadJobData();
    }
  }, [ processJobId, reloadJobData ]);

  // get the process job
  React.useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (getProcessJobResponse.data) {
      setProcessJob(getProcessJobResponse.data);

      if (getProcessJobResponse.data.finishedAt && TableRef.current) {
        TableRef.current.reloadTable();
      }

      if (getProcessJobResponse.data.status.match(/queued|in_progress|initialising/)) {
        if (!interval) {
          interval = setInterval(() => {
            reloadJobData();
          }, 10000);
        }
      }
    }

    if (getProcessJobResponse.error) {
      dispatch(setGlobalError(getProcessJobResponse.error));
    }

    if (interval) {
      clearInterval(interval);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [ getProcessJobResponse ]);

  // get the process job
  React.useEffect(() => {
    if (processJob) {
      if (processJob.status.match(/queued|in_progress/) !== null) {
        if (!importInterval.current) {
          importInterval.current = setInterval(() => {
            reloadJobData(true);
          }, 10000);
        }
      } else if (typeof importInterval.current === "number") {
        clearInterval(importInterval.current);
      }

      return () => {
        if (typeof importInterval.current === "number") {
          clearInterval(importInterval.current);
        }
      };
    }

    return () => null;
  }, [ processJob ]);

  // refetches errors
  const onFetchErrorData = React.useCallback(
    (filters: FilterParams<IProcessJobError>, pagination: PaginatedParams<IProcessJobError>) => {
      if (processJobId) {
        getProcessJobErrorsRequest({
          processJobId,
          filters,
          pagination
        });
      }
    },
    [ processJobId ]
  );

  React.useEffect(() => {
    if (ignoreErrorResponse.data) {
      setProcessJob(ignoreErrorResponse.data);

      if (TableRef.current) {
        TableRef.current.resetPagination();
        TableRef.current.reloadTable();
      }
    } else if (ignoreErrorResponse.error) {
      toast.error(`Failed to resolve errors - ${ignoreErrorResponse.error.message}`);

      if (TableRef.current) {
        TableRef.current.resetPagination();
        TableRef.current.reloadTable();
      }
    }
  }, [ ignoreErrorResponse ]);

  React.useEffect(() => {
    if (retryErrorResponse.data) {
      setProcessJob(retryErrorResponse.data);

      if (TableRef.current) {
        TableRef.current.resetPagination();
        TableRef.current.reloadTable();
      }
    } else if (retryErrorResponse.error) {
      toast.error(`Failed to retry errors - ${retryErrorResponse.error.message}`);

      if (TableRef.current) {
        TableRef.current.resetPagination();
        TableRef.current.reloadTable();
      }
    }
  }, [ retryErrorResponse ]);

  /**
   * Bulk actions
   */
  const bulkActions = React.useCallback(
    (bulkState: BulkSelectState) => {
      if (processJob?.jobData && (processJob.jobData.type === "order-predictions" || processJob.jobData.type === "order-picks")) {
        return [] as IAction[];
      }

      return [
        {
          title: "Mark as resolved",
          action: () => ignoreErrorRequest({
            errorIds: _.values(bulkState),
            processJobId
          })
        },
        {
          title: "Retry",
          action: () => retryErrorRequest({
            errorIds: _.values(bulkState),
            processJobId
          })
        }
      ] as IAction[];
    },
    [
      ignoreErrorRequest,
      processJobId,
      retryErrorRequest
    ]
  );

  const allowedTabs: TabOptions[] = React.useMemo(() => {
    if (processJob) {
      const defaultTabs: TabOptions[] = [];

      if (hasErrors || (!canRetryErrors && !hasOrders && !hasWarnings)) {
        defaultTabs.push({
          title: "Unresolved Errors",
          content: () => (
            <>
              <Text as="p">
                Showing unresolved errors. To complete the import please mark errors as resolved or retry importing the errored lines.
              </Text>

              <Table
                bulkActionOptions={canRetryErrors ? bulkActions : undefined}
                onFetchData={onFetchErrorData}
                ref={TableRef}
                tableId={`order-import-${processJob.id}`}
                columns={getErrorRowColumnsByJob(processJob, props.terminology)}
                tableOf="Order Import errors"
                data={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.result : []}
                error={getProcessJobErrorsResponse.error}
                totalRows={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.count : 0}
                pages={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.pages : 0}
                loading={isLoading}
                defaultPagination={defaultPagination}
                defaultFilters={{ status: "error" }}
              />
            </>
          )
        });
      }

      if (canRetryErrors) {
        defaultTabs.push({
          title: "Resolved Errors",
          content: () => (
            <>
              <Text as="p">
                Showing previously resolved errors that require no furter action!
              </Text>

              <Table
                onFetchData={onFetchErrorData}
                ref={TableRef}
                tableId={`order-import-${processJob.id}`}
                columns={getErrorRowColumnsByJob(processJob, props.terminology)}
                tableOf="Resolved Errors"
                data={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.result : []}
                error={getProcessJobErrorsResponse.error}
                totalRows={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.count : 0}
                pages={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.pages : 0}
                loading={isLoading}
                defaultPagination={defaultPagination}
                defaultFilters={{ status: { $not: "error" } }}
              />
            </>
          )
        });
      }

      // add an orders tab if needed
      if (hasOrders) {
        if (processJob.name === "global-product-replacement") {
          defaultTabs.push({
            title: "Orders",
            content: () => (<RelatedOrdersTab processJobId={processJob.id} />)
          });
        } else {
          defaultTabs.push({
            title: "Orders",
            content: () => (<ImportedOrdersTab processJobId={processJob.id} />)
          });
        }
      }

      if (hasWarnings) {
        defaultTabs.push({
          title: "Warnings",
          content: () => (
            <Table
              bulkActionOptions={canRetryErrors ? bulkActions : undefined}
              onFetchData={onFetchErrorData}
              ref={TableRef}
              tableId={`price-import-warning-${processJob.id}`}
              columns={getErrorRowColumnsByJob(processJob, props.terminology)}
              tableOf="Price Import Warnings"
              data={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.result : []}
              error={getProcessJobErrorsResponse.error}
              totalRows={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.count : 0}
              pages={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.pages : 0}
              loading={isLoading}
              defaultPagination={defaultPagination}
              defaultFilters={{ status: "warn" }}
            />
          )
        });
      }

      return defaultTabs;
    }

    return [];
  }, [
    bulkActions,
    canRetryErrors,
    getProcessJobErrorsResponse.data,
    getProcessJobErrorsResponse.error,
    hasOrders,
    isLoading,
    onFetchErrorData,
    processJob,
    props.terminology
  ]);

  return (
    <Layout
      {...props}
      title={processJob?.name.split("-").join(" ") || "Job"}
    >
      {allowedTabs.length > 0 ? (
        <MainContentWrapper fill>
          {!!processJob && (
            <>
              {processJob.status === "queued" && (
                <Text as="p">
                  Your import has been queued. This page will automatically update to show progress.
                </Text>
              )}

              <TabbedNavigation
                tabs={allowedTabs}
              />
            </>
          )}
        </MainContentWrapper>
      ) : <></>}

      <InfoBarWrapper>
        {!!processJob && (
          <ProcessJobSidebar
            isLoading={false}
            data={processJob}
          />
        )}
      </InfoBarWrapper>
    </Layout>
  );
};

export default ViewQueueJobPage;
